import BigNumber from 'bignumber.js'
import dayjs, {
  ConfigType,
  extend,
} from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import duration, { DurationUnitType } from 'dayjs/plugin/duration'
import isToday from 'dayjs/plugin/isToday'
import isYesterday from 'dayjs/plugin/isYesterday'
import relativeTime from 'dayjs/plugin/relativeTime'
import tz from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

extend(utc)
extend(customParseFormat)
extend(relativeTime)
extend(tz)
extend(duration)
extend(isToday)
extend(isYesterday)

export const DEFAULT_DISPLAY = '-'
export const DEFAULT_NA_DISPLAY = 'N/A'

export const formatNAText = ({ defaultDisplay = DEFAULT_NA_DISPLAY } = {}) => (value?: string | number | null) => {
  return value === null ? defaultDisplay : value
}

export const formatPrice = ({
  defaultDisplay = DEFAULT_DISPLAY,
  currency = '🌟',
  decimal = 2,
  unit = '',
} = {}) => (value: BigNumber.Value | null | undefined) => {
  if (value === undefined || value === null) {
    return defaultDisplay
  }
  const bigNumber = new BigNumber(value)
  if (bigNumber.isNaN() || !bigNumber.isFinite()) {
    return defaultDisplay
  }

  if (bigNumber.isZero()) {
    return `${currency}0`
  }

  if (unit.toUpperCase() === 'K' && bigNumber.isGreaterThanOrEqualTo(1000)) {
    return `${currency}${bigNumber.dividedBy(1000).toFixed(decimal, BigNumber.ROUND_DOWN)}K`
  }

  return `${currency}${bigNumber.toFixed(decimal, BigNumber.ROUND_DOWN)}`
}

export interface FormatTimeParams {
  format?: string
  defaultDisplay?: string
  timezone?: string
}

export const formatTime = ({
  format,
  defaultDisplay,
  timezone,
}: FormatTimeParams = {}) => (value: ConfigType) => {
  const finalFormat = format || 'MMM. D, YYYY [at] h:mm A'

  if (value !== undefined && value !== null) {
    if (timezone !== undefined) {
      return dayjs(value).tz(timezone).format(finalFormat)
    }
    return dayjs(value).format(finalFormat)
  }
  return defaultDisplay || DEFAULT_DISPLAY
}

export const formatMessageDate = (date: string) => {
  const d = dayjs(date)
  if (d.isToday()) {
    return 'Today'
  } else if (d.isYesterday()) {
    return 'Yesterday'
  } else if (d.isAfter(dayjs().subtract(1, 'year'))) {
    return d.format('MMM D')  // 一年内显示 "Sep 12" 格式
  } else {
    return d.format('YYYY-MM-DD')
  }
}

export const formatChatTime = (value: ConfigType) => {
  const chatDate = dayjs(value)
  const format = chatDate.isToday() ? 'h:mm A' : 'MMM D'
  return formatTime({ format })(value)
}

export const formatPercentage = ({
  digit = 2,
  decimals = 0,
  symbol = '%',
  defaultDisplay = DEFAULT_DISPLAY,
} = {}) => (value?: number | bigint | BigNumber | null): string => {
  if (value === null || value === undefined) {
    return defaultDisplay
  }
  const bigNumber = new BigNumber(value.toString())
  if (bigNumber.isNaN()) {
    return defaultDisplay
  }
  return `${bigNumber.shiftedBy(digit).toFixed(decimals)}${symbol}`
}

export function formatCompactNumber(num: number) {
  if (num >= 1_000_000_000_000) {
    return (num / 1_000_000_000_000).toFixed(1).replace(/\.0$/, '') + 'T'
  }
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'k'
  }
  return num.toString()
}

export interface FormatDurationToHMSOptions {
  unit?: DurationUnitType
  format?: 'unit' | 'colon'
}

export const formatDurationToH_M_S = ({
  unit = 'milliseconds',
  format = 'unit',
}: FormatDurationToHMSOptions = {}) => (value: number | undefined) => {
  if (value === undefined) {
    return DEFAULT_DISPLAY
  }

  const duration = dayjs.duration(value, unit)
  const h = Math.floor(duration.asHours())
  const m = duration.minutes()
  const s = duration.seconds()
  if (format === 'unit') {
    return [
      h ? `${h}h` : null,
      m ? `${m}m` : null,
      `${s}s`,
    ].filter(Boolean).join(' ')
  }
  return [
    h || 0,
    m || 0,
    s,
  ].map((value) => value.toString().padStart(2, '0')).join(':')
}
